// Uygulama kabuğu — ref_files/btys-erp-01-stok-karti.html'in <body> yapısı birebir:
//   .shell > aside.side(.brand + nav.nav + .nav-collapse) + .main(header.top + .tabbar + main.content)
//
// Menü öğeleri rol izin matrisine göre filtrelenir (ekran 14): erişimi olmayan
// modül menüde HİÇ görünmez. Sunucu tarafı zaten 403 döner; buradaki filtre
// kullanıcıyı boş bir kapıya götürmemek için.

import { useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useOturum } from '../lib/oturum'
import { useYetki, type Modul } from '../lib/yetki'
import { DrawerSaglayici, DrawerYuvasi } from './Drawer'
import { SekmeCubugu, SekmePanolari, SekmeSaglayici } from './Sekmeler'
import { toast, ToastYuvasi } from './Toast'

/**
 * Rota → ekran sınıfı (`.sf-NN`), `.shell` üzerine yazılır.
 *
 * Referans zinciri BİRİKİMLİ DEĞİL: aynı sınıf ekrandan ekrana farklı ölçüyle
 * tanımlanmış (ör. `.list-grid` 01–05'te 396px, 06'dan sonra 278px). Tek dosyada
 * birleşince son tanım kazandığı için erken ekranların ölçüleri kayboluyordu.
 * Fark `ekran-ustleri.css`'te bu sınıfın altında geri veriliyor.
 *
 * Sınıfı `.shell`e koymanın sebebi: farkların bir kısmı sayfa bileşeninin DIŞINDA
 * kalan kabuk elemanlarına ait (`.content` dolgusu gibi).
 */
const EKRAN_SINIFI: Record<string, string> = {
  '/': 'sf-10',
  '/stok-kartlari': 'sf-01',
  '/mal-kabul': 'sf-02',
  '/laboratuvar': 'sf-03',
  '/cariler': 'sf-04',
  '/uniteler': 'sf-05',
  '/uretim-beyani': 'sf-06',
  '/besleme': 'sf-07',
  '/filo': 'sf-08',
  '/stok-hareketleri': 'sf-09',
  '/profil': 'sf-13',
  '/ayarlar': 'sf-14',
}

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const
const ikon = (d: string, ek?: ReactNode) => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
    <path d={d} />
    {ek}
  </svg>
)

/**
 * Çıkış onay katmanı — referans <c>cikisOnay()</c> (13-profil.html:570-576).
 * Kabuk seviyesinde: hem kullanıcı menüsünden hem ekran 13'ün "Çıkış yap"
 * düğmesinden AYNI katman açılıyor. Onaylanınca /cikis'e gidiliyor; oturumu
 * orada kapatıyoruz çünkü çıkış ekranı özeti göstermek zorunda (ekran 12).
 */
function CikisOnayi() {
  const { cikisOnayiAcik, cikisOnayiKapat, durum } = useOturum()
  const yonlendir = useNavigate()

  useEffect(() => {
    if (!cikisOnayiAcik) return
    const dinle = (e: KeyboardEvent) => { if (e.key === 'Escape') cikisOnayiKapat() }
    document.addEventListener('keydown', dinle)
    return () => document.removeEventListener('keydown', dinle)
  }, [cikisOnayiAcik, cikisOnayiKapat])

  if (!durum) return null

  return (
    <>
      <div className={cikisOnayiAcik ? 'scrim on' : 'scrim'} onClick={cikisOnayiKapat} />
      <aside
        className={cikisOnayiAcik ? 'drawer sm on' : 'drawer sm'}
        role="dialog"
        aria-modal="true"
        aria-label="Çıkış yapılsın mı?"
        aria-hidden={!cikisOnayiAcik}
      >
        <div className="dr-h">
          <div>
            <div className="dr-t">Çıkış yapılsın mı?</div>
            <div className="dr-s">Bekleyen taslaklar otomatik kaydedilir</div>
          </div>
          <button className="icon-btn dr-x" type="button" onClick={cikisOnayiKapat} aria-label="Kapat">
            <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="dr-b">
          <div className="info-box">
            <div>
              Çıkışta oturum kaydı denetim izine işlenir; kilitlemediğiniz beyan günleri
              taslak kalır ve kaldığı yerden devam eder.
            </div>
          </div>
        </div>
        <div className="dr-f">
          <button className="btn btn-sec" type="button" onClick={cikisOnayiKapat}>
            Vazgeç
          </button>
          <button
            className="btn btn-danger"
            type="button"
            onClick={() => {
              cikisOnayiKapat()
              yonlendir('/cikis')
            }}
          >
            Çıkış yap
          </button>
        </div>
      </aside>
    </>
  )
}

type MenuOge = {
  baslik: string
  yol?: string
  modul?: Modul
  ikon: ReactNode
  rozet?: number
}

const PANEL: MenuOge = {
  baslik: 'Kontrol Paneli',
  yol: '/',
  ikon: (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  ),
}

const GRUPLAR: { baslik: string; ogeler: MenuOge[] }[] = [
  {
    baslik: 'Ana Veri',
    ogeler: [
      {
        baslik: 'Stok Kartları', yol: '/stok-kartlari', modul: 'StokKartlari',
        ikon: ikon('M2 4h20v16H2zM2 10h20M7 15h5'),
      },
      {
        baslik: 'Cariler', yol: '/cariler', modul: 'FiloCariler',
        ikon: ikon('M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', <circle cx="12" cy="7" r="4" />),
      },
      { baslik: 'Üniteler', yol: '/uniteler', modul: 'Uniteler', ikon: ikon('M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6') },
      {
        // Tesis yapılandırması — Üniteler ile aynı izin kümesi (okuma herkeste,
        // yazma yalnız tesis sorumlusunda), bu yüzden aynı grupta duruyor.
        baslik: 'SCADA Entegrasyonu', yol: '/entegrasyon', modul: 'Entegrasyon',
        ikon: ikon(
          'M4.9 19.1a10 10 0 0 1 0-14.2M7.8 16.2a6 6 0 0 1 0-8.4M16.2 7.8a6 6 0 0 1 0 8.4M19.1 4.9a10 10 0 0 1 0 14.2',
          <circle cx="12" cy="12" r="2" />,
        ),
      },
    ],
  },
  {
    baslik: 'Operasyon',
    ogeler: [
      { baslik: 'Mal Kabul', yol: '/mal-kabul', modul: 'MalKabul', ikon: ikon('M1 3h15v13H1zM16 8h4l3 3v5h-7z') },
      { baslik: 'Laboratuvar', yol: '/laboratuvar', modul: 'Laboratuvar', ikon: ikon('M9 2v7L4 20a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-5-11V2M7 2h10') },
      { baslik: 'Stok', yol: '/stok-hareketleri', modul: 'Hareketler', ikon: ikon('M21 16V8l-9-5-9 5v8l9 5 9-5zM3.3 7 12 12l8.7-5M12 22V12') },
      { baslik: 'Besleme', yol: '/besleme', modul: 'Besleme', ikon: ikon('M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5') },
      { baslik: 'Üretim Beyanı', yol: '/uretim-beyani', modul: 'Beyan', ikon: ikon('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h6') },
      { baslik: 'Filo', yol: '/filo', modul: 'FiloCariler', ikon: ikon('M1 6h13v10H1zM14 9h4l3 3v4h-7z', <><circle cx="5" cy="19" r="2" /><circle cx="18" cy="19" r="2" /></>) },
    ],
  },
  {
    baslik: 'Hesap',
    ogeler: [
      {
        baslik: 'Profilim', yol: '/profil',
        // Modül izni YOK: herkes kendi kaydını görür (ekran 13).
        ikon: ikon('M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', <circle cx="12" cy="7" r="4" />),
      },
      {
        baslik: 'Ayarlar', yol: '/ayarlar', modul: 'Ayarlar',
        ikon: ikon('M12 1v3M12 20v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M1 12h3M20 12h3M4.9 19.1 7 17M17 7l2.1-2.1', <circle cx="12" cy="12" r="3" />),
      },
    ],
  },
  {
    baslik: 'Analiz',
    ogeler: [
      { baslik: 'Raporlar', modul: 'Beyan', ikon: ikon('M3 3v18h18M7 15l4-4 3 3 5-6') },
          ],
  },
]

export default function Kabuk({
  children,
  listeBasligi = 'Liste',
}: {
  children: ReactNode
  /** Sekme çubuğundaki kapatılamaz ilk sekmenin adı (referans: "Stok Kartları"). */
  listeBasligi?: string
}) {
  const { durum, cikisOnayiAc, izinlerTazelenemedi, izinleriTazele } = useOturum()
  const { izinVar } = useYetki()
  const yonlendir = useNavigate()
  const konum = useLocation()
  const [menuAcik, setMenuAcik] = useState(false)
  const [daraltilmis, setDaraltilmis] = useState(false)

  // Menü dışına tıklayınca kapansın — referans ekran da böyle davranıyor.
  useEffect(() => {
    if (!menuAcik) return
    const dinle = () => setMenuAcik(false)
    document.addEventListener('click', dinle)
    return () => document.removeEventListener('click', dinle)
  }, [menuAcik])

  if (!durum) return null

  const bas = durum.kullanici.ad
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  const gorunur = (o: MenuOge) => !o.modul || izinVar(o.modul)

  /**
   * Menü öğesi seçili mi. Alt rotalar da öğeyi işaretler
   * (`/entegrasyon/etiketler` → "SCADA Entegrasyonu"), yoksa kullanıcı alt
   * görünüme geçtiğinde menüde hiçbir şey seçili kalmıyordu.
   *
   * Kök `/` önek kuralının DIŞINDA: aksi hâlde her sayfada "Kontrol Paneli" de
   * seçili görünürdü.
   */
  const secili = (yol: string | undefined) =>
    !!yol && (konum.pathname === yol || (yol !== '/' && konum.pathname.startsWith(`${yol}/`)))

  const MenuButonu = ({ o }: { o: MenuOge }) => (
    <button
      className={secili(o.yol) ? 'nav-i on' : 'nav-i'}
      type="button"
      title={o.baslik}
      onClick={() =>
        o.yol ? yonlendir(o.yol) : toast(`${o.baslik} ekranı sonraki fazda geliyor`)
      }
    >
      {o.ikon}
      <span className="nav-t">{o.baslik}</span>
      {o.rozet ? <span className="nav-badge">{o.rozet}</span> : null}
    </button>
  )

  return (
    <DrawerSaglayici>
      <SekmeSaglayici listeBasligi={listeBasligi}>
        <div className={['shell', daraltilmis ? 'min' : '', EKRAN_SINIFI[konum.pathname] ?? '']
          .filter(Boolean)
          .join(' ')}>
          <aside className="side">
            <div className="brand">
              <div className="brand-mark">B</div>
              <div className="brand-txt">
                <div className="brand-name">BTYS</div>
                <div className="brand-sub">Biyogaz Yönetim</div>
              </div>
            </div>

            <nav className="nav">
              <MenuButonu o={PANEL} />
              {GRUPLAR.map((g) => {
                const ogeler = g.ogeler.filter(gorunur)
                // Tüm öğeleri gizlenen grup başlığı da görünmemeli.
                if (ogeler.length === 0) return null
                return (
                  <div className="nav-grp" key={g.baslik}>
                    <div className="nav-grp-t">{g.baslik}</div>
                    {ogeler.map((o) => <MenuButonu key={o.baslik} o={o} />)}
                  </div>
                )
              })}
            </nav>

            <div className="nav-collapse">
              <button
                className="nav-i"
                type="button"
                title="Menüyü daralt/genişlet"
                onClick={() => setDaraltilmis((d) => !d)}
              >
                <svg className="cchev" width="15" height="15" viewBox="0 0 24 24" {...S}>
                  <path d="m15 18-6-6 6-6" />
                </svg>
                <span className="nav-t">Menüyü daralt</span>
              </button>
            </div>
          </aside>

          <div className="main">
            <header className="top">
              <div className="tesis">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#17427E" strokeWidth="2">
                  <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
                </svg>
                <div>
                  {/*
                    Sabit yedek KALDIRILDI. Önceden burada 'Yeşil Enerji A.Ş.'
                    yazıyordu ve alan hiç doldurulmadığı için HER müşteri demo
                    şirketin adını görüyordu. Unvan bilinmiyorsa hiçbir şey
                    yazmamak, yanlış bir şirket adı yazmaktan iyidir.
                  */}
                  <b>{durum.tenantUnvan || '—'}</b>{' '}
                  {/* Süper adminde tesis yok; kabuk zaten ona açılmıyor ama
                      tip güvenliği için kapsam adı boş geçiliyor. */}
                  {durum.tesis && <span>· {durum.tesis.ad}</span>}
                </div>
                <svg width="12" height="12" viewBox="0 0 24 24" {...S}>
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </div>

              <div className="gsearch">
                <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input placeholder="Kayıt, lot, plaka veya cari ara…" />
                <kbd>⌘K</kbd>
              </div>

              <div className="top-right">
                <button className="icon-btn" type="button" title="Bildirimler">
                  <svg width="16" height="16" viewBox="0 0 24 24" {...S}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
                  </svg>
                </button>
                <div
                  className="user"
                  onClick={(e) => {
                    e.stopPropagation()
                    setMenuAcik((a) => !a)
                  }}
                >
                  <div className="avatar">{bas}</div>
                  <div>
                    <div className="user-n">{durum.kullanici.ad}</div>
                    <div className="user-r">{durum.kullanici.unvan ?? durum.kullanici.rol}</div>
                  </div>
                  <div className={menuAcik ? 'umenu on' : 'umenu'}>
                    <a
                      href="/profil"
                      onClick={(e) => {
                        e.preventDefault()
                        yonlendir('/profil')
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      Profilim
                    </a>
                    {izinVar('Ayarlar') && (
                      <a
                        href="/ayarlar"
                        onClick={(e) => {
                          e.preventDefault()
                          yonlendir('/ayarlar')
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
                          <circle cx="12" cy="12" r="3" />
                          <path d="M12 1v3M12 20v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M1 12h3M20 12h3M4.9 19.1 7 17M17 7l2.1-2.1" />
                        </svg>
                        Ayarlar
                      </a>
                    )}
                    <div className="usep" />
                    {/* Oturumu BURADA kapatmıyoruz: önce onay katmanı açılıyor
                        (referans cikisOnay()), kapatma /cikis'in işi — çıkış
                        ekranı özeti (kullanıcı, tesis, kapanış saati) göstermek
                        zorunda. */}
                    <a
                      className="dgr"
                      href="/cikis"
                      onClick={(e) => {
                        e.preventDefault()
                        cikisOnayiAc()
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                      </svg>
                      Çıkış yap
                    </a>
                  </div>
                </div>
              </div>
            </header>

            <SekmeCubugu />

            <main className="content">
              <SekmePanolari liste={children} />
            </main>

            {/*
              .main grid'i 46px/36px/1fr/24px — dördüncü satır statusbar.
              Basılmadığında son 24px BOŞ kalıyordu; referansın her ekranında
              var (13-profil.html:563-566).
            */}
            <footer className="statusbar">
              <span>
                <b>BTYS</b> · {durum.tenantUnvan || 'Biyogaz Tesisi Yönetim Sistemi'}
              </span>
              {durum.tesis && <span className="sep">{durum.tesis.ad}</span>}
              <span>
                Oturum: <b>{durum.kullanici.ad}</b>
              </span>
              {/*
                Yetki haritası tazelenemediyse GÖRÜNÜR uyarı. Harita
                localStorage'da saklanıyor; tazeleme başarısız olunca eski
                harita kalıyor ve YENİ MODÜLLERİN menü öğesi sessizce
                görünmüyor. Kullanıcı eksikliği görüp nedenini göremiyordu —
                bu satır nedeni söylüyor ve tek tıkla yeniden deniyor.
              */}
              {izinlerTazelenemedi ? (
                <button
                  className="badge b-amber"
                  type="button"
                  style={{ border: 'none', cursor: 'pointer', font: 'inherit' }}
                  title={
                    'Sunucudan yetki haritası alınamadı; menü eksik görünebilir. ' +
                    'Yeniden denemek için tıklayın.'
                  }
                  onClick={() => { void izinleriTazele() }}
                >
                  Yetki haritası eski — menü eksik olabilir
                </button>
              ) : null}
            </footer>
          </div>
        </div>

        <CikisOnayi />
        <DrawerYuvasi />
        <ToastYuvasi />
      </SekmeSaglayici>
    </DrawerSaglayici>
  )
}
